import { useCallback, useEffect, useMemo, useState } from 'react';
import { Play, AlertTriangle } from 'lucide-react';
import ModulLayout from '../layout/ModulLayout';
import ListToolbar, { PohledPill } from '../layout/ListToolbar';
import VyplneniChecklistu from './VyplneniChecklistu';
import { SECTION_COLOR, SECTION_BG, STAV_BARVY, STAV_LABELS, Badge } from './ui';

/**
 * Checklisty → Checklisty: všechny vyplněné a rozpracované listy (sloučený
 * dřívější Přehled a Historie). Norma §1: seznam · detail · třetí sloupec.
 * Seznam je seskupený podle směny (den + šablona směny), ruční checklisty
 * zvlášť. Detail = VyplneniChecklistu (dřív modal).
 */
const dnes = () => new Date().toDateString();
const POHLEDY = [
    { id: 'dnes', label: 'Dnes', patri: c => new Date(c.created_at).toDateString() === dnes() },
    { id: 'rozpracovane', label: 'Rozpracované', patri: c => ['otevreno', 'probihajici'].includes(c.stav) },
    { id: 'odchylka', label: 'S odchylkou', upozorneni: true, patri: c => c.ma_alert },
    { id: 'prerusene', label: 'Přerušené', patri: c => c.stav === 'preruseno' },
    { id: 'vse', label: 'Posledních 60 dní', patri: () => true },
];
const den = (d) => new Date(d).toLocaleDateString('cs-CZ', { weekday: 'short', day: 'numeric', month: 'numeric' });
const cas = (d) => new Date(d).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' });

export default function ChecklistySeznam({ isDark, isMobile, cardStyle, inputStyle, mutedColor, onSpustit, obnova = 0, otevritId = null, rezervaDole = null }) {
    const [data, setData] = useState(null);
    const [pohled, setPohled] = useState(null);
    const [hledani, setHledani] = useState('');
    const [vybranyId, setVybranyId] = useState(otevritId);

    useEffect(() => { if (otevritId) setVybranyId(otevritId); }, [otevritId]);
    const nacti = useCallback(() => {
        fetch('/api/checklisty', { headers: { Accept: 'application/json' } }).then(r => (r.ok ? r.json() : [])).then(setData).catch(() => setData([]));
    }, []);
    useEffect(() => { nacti(); }, [nacti, obnova]);

    const vse = data || [];
    const pocty = useMemo(() => Object.fromEntries(POHLEDY.map(p => [p.id, vse.filter(p.patri).length])), [vse]);
    const aktivni = POHLEDY.find(p => p.id === (pohled || (pocty.dnes > 0 ? 'dnes' : 'vse'))) || POHLEDY[4];
    const hledat = hledani.trim().toLowerCase();
    const seznam = vse.filter(c => aktivni.patri(c) && (!hledat || `${c.nazev} ${c.vyplnil?.jmeno || ''} ${c.vyplnil?.prijmeni || ''} ${c.smena?.sablon?.kod || ''} ${c.smena?.sablon?.nazev || ''}`.toLowerCase().includes(hledat)));

    // Seskupení podle směny: den + šablona směny; bez směny = ruční
    const skupiny = useMemo(() => {
        const m = new Map();
        for (const c of seznam) {
            const k = c.smena ? `${c.smena.datum?.slice(0, 10)}|${c.smena.sablon?.kod || c.smena.id}` : `rucni|${new Date(c.created_at).toDateString()}`;
            if (!m.has(k)) m.set(k, {
                klic: k,
                nazev: c.smena
                    ? `${den(c.smena.datum)} · ${[c.smena.sablon?.kod, c.smena.sablon?.nazev].filter(Boolean).join(' ') || 'směna'}`
                    : `${den(c.created_at)} · spuštěno ručně`,
                polozky: [],
            });
            m.get(k).polozky.push(c);
        }
        return [...m.values()];
    }, [seznam]);

    const cara = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle)';
    const muted = mutedColor;

    const radek = (c, siroky) => {
        const pct = c.pocet_polozek > 0 ? Math.round((c.pocet_vyplnenych / c.pocet_polozek) * 100) : 0;
        const vybrany = c.id === vybranyId;
        const kdo = c.vyplnil ? `${c.vyplnil.jmeno} ${c.vyplnil.prijmeni}` : '—';
        return (
            <button key={c.id} type="button" onClick={() => setVybranyId(c.id)} className="w-full text-left flex items-center"
                style={{ gap: 12, padding: isMobile ? '12px 16px' : '9px 14px', minHeight: isMobile ? 60 : 52, background: vybrany ? SECTION_BG : 'transparent', boxShadow: vybrany ? `inset 3px 0 0 ${SECTION_COLOR}` : 'none', border: 'none', borderBottom: `1px solid ${cara}`, cursor: 'pointer' }}>
                <div className="flex-1 min-w-0">
                    <p className="truncate font-semibold" style={{ margin: 0, color: 'var(--text-primary)', fontSize: isMobile ? 15 : 13.5 }}>{c.nazev}</p>
                    {!siroky && <p className="truncate" style={{ margin: '2px 0 0', fontSize: isMobile ? 12.5 : 11.5, color: muted }}>{kdo} · {cas(c.created_at)}</p>}
                </div>
                {siroky && <span className="truncate" style={{ width: 170, fontSize: 12, color: muted }}>{kdo}</span>}
                {siroky && <span style={{ width: 90, fontSize: 12, color: muted, fontVariantNumeric: 'tabular-nums' }}>{cas(c.created_at)}</span>}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                    {(siroky || !isMobile) && (
                        <div style={{ width: siroky ? 90 : 50, height: 4, borderRadius: 2, background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' }}>
                            <div style={{ width: `${pct}%`, height: '100%', borderRadius: 2, background: SECTION_COLOR }} />
                        </div>
                    )}
                    <span style={{ fontSize: 11, color: muted, minWidth: 30, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{c.pocet_vyplnenych}/{c.pocet_polozek}</span>
                </div>
                {c.ma_alert && <AlertTriangle size={14} style={{ color: '#EF4444', flexShrink: 0 }} aria-label="Odchylka" />}
                <Badge color={STAV_BARVY[c.stav]}>{STAV_LABELS[c.stav]}</Badge>
            </button>
        );
    };

    const seznamPanel = ({ siroky }) => (
        <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
            <ListToolbar
                siroky={siroky} kompaktni={isMobile} sklo isDark={isDark} mutedColor={muted} inputStyle={inputStyle} barva={SECTION_COLOR}
                hledani={{ value: hledani, onChange: setHledani, placeholder: 'Hledat checklist, člověka, směnu…', placeholderKratky: 'Hledat' }}
                akce={onSpustit ? [{ id: 'spustit', label: 'Spustit checklist', Icon: Play, primarni: true, onClick: onSpustit }] : []}
                pohledy={!siroky && !isMobile ? POHLEDY.map(p => ({ id: p.id, label: p.label, pocet: pocty[p.id], upozorneni: p.upozorneni, barva: p.upozorneni ? '#EF4444' : undefined })) : null}
                pohledAktivni={aktivni.id}
                onPohled={setPohled}
                pohled={!siroky && !isMobile ? null : POHLEDY.map(p => (
                    <PohledPill key={p.id} label={p.label} pocet={pocty[p.id]} aktivni={aktivni.id === p.id}
                        upozorneni={p.upozorneni && pocty[p.id] > 0} onClick={() => setPohled(p.id)}
                        isDark={isDark} mutedColor={muted} barva={SECTION_COLOR} kompaktni={isMobile} />
                ))}
            />
            <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0 }}>
                {data === null && <p className="text-center text-sm" style={{ color: muted, padding: 32 }}>Načítám…</p>}
                {data && seznam.length === 0 && (
                    <p className="text-center text-sm" style={{ color: muted, padding: '32px 16px' }}>
                        {hledat ? 'Nic se neshoduje.' : aktivni.id === 'odchylka' ? 'Žádná odchylka — to je dobrá zpráva.' : 'Nic tu není.'}
                    </p>
                )}
                {skupiny.map(g => (
                    <div key={g.klic}>
                        <div className="flex justify-between" style={{ padding: '10px 14px 6px', fontSize: 10.5, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: muted, borderBottom: `1px solid ${cara}` }}>
                            <span className="truncate">{g.nazev}</span><span>{g.polozky.length}</span>
                        </div>
                        {g.polozky.map(c => radek(c, siroky))}
                    </div>
                ))}
                {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
            </div>
        </div>
    );

    const detailPanel = () => (
        <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
            <VyplneniChecklistu key={vybranyId} checklistId={vybranyId} isDark={isDark} isMobile={isMobile} mutedColor={muted} iStyle={inputStyle}
                onZmena={nacti} onZpet={() => setVybranyId(null)} rezervaDole={isMobile ? rezervaDole : null} />
        </div>
    );

    // Třetí sloupec: dnešek v číslech a co je potřeba dořešit
    const panel = () => {
        const dnesni = vse.filter(POHLEDY[0].patri);
        const kDoreseni = [
            ...vse.filter(c => c.ma_alert && ['otevreno', 'probihajici', 'dokonceno'].includes(c.stav) && POHLEDY[0].patri(c))
                .map(c => ({ id: c.id, text: `${c.nazev} — odchylka`, kdo: c.vyplnil ? `${c.vyplnil.jmeno} ${c.vyplnil.prijmeni}` : null, cervene: true })),
            ...vse.filter(c => c.stav === 'preruseno' && new Date(c.created_at) > new Date(Date.now() - 7 * 864e5))
                .map(c => ({ id: c.id, text: `${c.nazev} — přerušeno`, kdo: den(c.created_at), cervene: false })),
        ].slice(0, 8);
        const r = (l, h, cervene) => (
            <div className="flex justify-between text-[13px]" style={{ minHeight: 30, alignItems: 'center', borderBottom: `1px solid ${cara}` }}>
                <span style={{ color: muted }}>{l}</span><b style={{ color: cervene && h > 0 ? '#F87171' : 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{h}</b>
            </div>
        );
        return (
            <div className="rounded-xl" style={{ ...cardStyle, padding: 16, display: 'flex', flexDirection: 'column', gap: 4 }}>
                <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: muted, margin: '0 0 6px' }}>Dnes</p>
                {r('Checklistů', dnesni.length)}
                {r('Hotovo', dnesni.filter(c => c.stav === 'dokonceno').length)}
                {r('Rozpracované', dnesni.filter(c => ['otevreno', 'probihajici'].includes(c.stav)).length)}
                {r('S odchylkou', dnesni.filter(c => c.ma_alert).length, true)}
                <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: muted, margin: '16px 0 6px' }}>K dořešení</p>
                {kDoreseni.length === 0 && <p className="text-xs" style={{ color: muted, margin: 0 }}>Nic.</p>}
                {kDoreseni.map(k => (
                    <button key={k.id + k.text} type="button" onClick={() => setVybranyId(k.id)} className="text-left"
                        style={{ padding: '8px 10px', borderRadius: 9, fontSize: 12, cursor: 'pointer', marginBottom: 6,
                            border: `1px solid ${k.cervene ? 'rgba(239,68,68,0.45)' : 'rgba(245,158,11,0.45)'}`,
                            background: k.cervene ? 'rgba(239,68,68,0.08)' : 'rgba(245,158,11,0.08)', color: 'var(--text-primary)' }}>
                        {k.text}{k.kdo && <span style={{ display: 'block', color: muted, fontSize: 11 }}>{k.kdo}</span>}
                    </button>
                ))}
            </div>
        );
    };

    return (
        <div className="flex flex-col" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <ModulLayout isMobile={isMobile} vybrano={!!vybranyId} mobilRezim mobilPanel={false} panelVzdy barva={SECTION_COLOR} isDark={isDark}
                seznam={seznamPanel} detail={detailPanel} panel={panel} />
        </div>
    );
}
